import { getEnv, getRoot, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { SupportedSoilTextures } from '@app/_components/lf-map/mapTypes';
import i18n from '@services/next-i18n';
import { DateFormats, formatLatLon, isPresent } from '@farmai-shared/LFUtils';
import { soilTypeOptions } from '@app/monitoring/_components/soil/SoilDropdown';
import { AbstractMapLayerBase } from './MapLayerBase';
import { withDateProcessors } from './DateTimeUtility';
import { IRootStoreAny } from './rootUtilType';
export const SoilSample = withDateProcessors(AbstractMapLayerBase.named('SoilSample').props({
  soilTexture: types.frozen<SupportedSoilTextures>(),
  location: types.array(types.number),
  elements: types.array(types.model({
    unit: types.string,
    value: types.number,
    judgement: types.maybeNull(types.string),
    codename: types.string,
    label: types.string,
    thresholds: types.optional(types.maybeNull(types.frozen<{
      low?: {
        min?: number;
        max?: number;
      };
      adequate?: {
        min?: number;
        max?: number;
      };
      high?: {
        min?: number;
        max?: number;
      };
    }>()), null)
  }))
}).views(self => ({
  get root() {
    // in certain situations, an environment can be passed to help store some information
    const root = getRoot<IRootStoreAny>(self);
    return isPresent(root?.uiStore) ? root : getEnv<IRootStoreAny>(self);
  },
  get locationOption() {
    return {
      label: formatLatLon(self.location).join(', '),
      value: self.id,
      cropzoneId: self.cropzone_id,
      color: soilTypeOptions.find(option => option.value === self.soilTexture)?.color || '#000'
    };
  }
})).views(self => ({
  get dateOption() {
    const cropzone = self.root.uiStore.currentOrganization.cropzones.get(self.cropzone_id);
    return cropzone && {
      label: self.datetimeZ.toFormat(DateFormats.DATE_SHORT),
      value: self.id,
      cropzoneId: self.cropzone_id
    };
  },
  get characteristics() {
    const agrocareElements = ['A_PH_WA', 'A_C_OF', 'A_CLAY_MI', 'A_CEC_CO', 'A_SAND_MI', 'A_H2O_2MM105', 'A_AL_RT', 'MC', 'SOM', 'EC'];
    const lfElements = ['PH_WATER', 'N_T_DC', 'SOC_WB', 'SOM_WB', 'CEC_AS', 'EC_WATER', 'MC_HAO', 'BD_CORE', 'SAND_HM', 'SILT_HM', 'CLAY_HM', 'SSL_EC'];
    const characteristicCodenames = [...lfElements, ...agrocareElements];
    return characteristicCodenames.reduce<ElementModelType[]>((acc, codename) => {
      const elements = self.elements.filter(e => e.codename === codename);
      elements.forEach(element => {
        const units = elements.map(e => e.unit);
        acc.push({
          label: i18n.t(element.codename),
          value: element.value,
          judgement: element.judgement,
          codename: element.codename,
          unit: element.unit,
          units: [...new Set(units)],
          thresholds: element.thresholds
        });
      });
      return acc;
    }, []);
  },
  get nutrition() {
    const agrocareElements = ['A_N_RT', 'A_P_M3', 'A_K_CO', 'A_CA_CO', 'A_MG_CO', 'A_FE_RT'];
    const lfElements = ['N_AVAIL', 'N_NO3', 'P_A_B2', 'K_AAC', 'CA_AAC', 'MG_AAC'];
    const nutritionCodenames = [...lfElements, ...agrocareElements];
    return nutritionCodenames.reduce<ElementModelType[]>((acc, codename) => {
      const elements = self.elements.filter(e => e.codename === codename);
      elements.forEach(element => {
        const units = elements.map(e => e.unit);
        acc.push({
          label: i18n.t(element.codename),
          value: element.value,
          judgement: element.judgement,
          codename: element.codename,
          unit: element.unit,
          units: [...new Set(units)],
          thresholds: element.thresholds
        });
      });
      return acc;
    }, []);
  }
})));
export type ElementModelType = {
  label: string;
  value: any;
  judgement: any;
  codename: string;
  unit: string;
  units: string[];
  thresholds?: {
    low?: {
      min?: number;
      max?: number;
    };
    adequate?: {
      min?: number;
      max?: number;
    };
    high?: {
      min?: number;
      max?: number;
    };
  } | null;
};
export type ISoilSample = Instance<typeof SoilSample>;
export type ISoilSampleSnapshotIn = SnapshotIn<typeof SoilSample>;